// /src/pages/private/members/MembersOnboardingWelcome.jsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  onboardingBackgroundKind,
  sanitizeHexColor,
} from '../../../utils/onboardingLandingUi';
import MembersOnboardingHeroBackground, { clamp01 } from './onboarding/MembersOnboardingHeroBackground';

const DEFAULT_COPY = {
  titleTop: 'Welcome to',
  titleBrandA: 'Pallas',
  titleBrandB: 'Invictus',
  subtitle: 'Your journey begins here.',
  description:
    'You are now part of something greater. Complete your onboarding to become an active member of our community and unlock your full potential.',
};

export default function MembersOnboardingWelcome() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const brandHostRef = useRef(null);
  const mediaLayerRef = useRef(null);

  const onStart = useCallback(() => {
    navigate('/members/onboarding/steps');
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get('/api/user/onboarding/landing-page', {
          withCredentials: true,
        });
        if (!cancelled) setPayload(data || {});
      } catch (e) {
        if (!cancelled) {
          setError('Unable to load onboarding content.');
          setPayload({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = useMemo(() => {
    const p = payload || {};
    return {
      titleTop: (p.titleTop && String(p.titleTop).trim()) || DEFAULT_COPY.titleTop,
      titleBrandA: (p.titleBrandA && String(p.titleBrandA).trim()) || DEFAULT_COPY.titleBrandA,
      titleBrandB: (p.titleBrandB && String(p.titleBrandB).trim()) || DEFAULT_COPY.titleBrandB,
      subtitle: (p.subtitle && String(p.subtitle).trim()) || DEFAULT_COPY.subtitle,
      description: (p.description && String(p.description).trim()) || DEFAULT_COPY.description,
    };
  }, [payload]);

  const hexA = useMemo(() => sanitizeHexColor(payload?.titleBrandAColor), [payload]);
  const hexB = useMemo(() => sanitizeHexColor(payload?.titleBrandBColor), [payload]);

  useLayoutEffect(() => {
    const el = brandHostRef.current;
    if (!el) return;
    if (hexA) el.style.setProperty('--mob-brand-a', hexA);
    else el.style.removeProperty('--mob-brand-a');
    if (hexB) el.style.setProperty('--mob-brand-b', hexB);
    else el.style.removeProperty('--mob-brand-b');
  }, [hexA, hexB]);

  const bgMedia = payload?.backgroundMedia || null;

  useLayoutEffect(() => {
    const el = mediaLayerRef.current;
    if (!el) return;
    const fx = clamp01(Number(bgMedia?.focalX));
    const fy = clamp01(Number(bgMedia?.focalY));
    el.style.setProperty('--mob-focal-x', `${fx * 100}%`);
    el.style.setProperty('--mob-focal-y', `${fy * 100}%`);
  }, [bgMedia]);

  const hasBackground = onboardingBackgroundKind(bgMedia) !== 'none';
  const heroClassName = [
    'members-onboarding-welcome__hero',
    hasBackground ? 'members-onboarding-welcome__hero--has-media' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="private-pages members-onboarding-welcome">
      <div className={heroClassName}>
        <div ref={mediaLayerRef} className="members-onboarding-welcome__media-layer">
          <MembersOnboardingHeroBackground media={bgMedia} />
        </div>
        <div className="members-onboarding-welcome__scrim" aria-hidden />
        <div ref={brandHostRef} className="members-onboarding-welcome__hero-inner">
          {loading ? <p className="members-onboarding-welcome__loading">Loading…</p> : null}
          {error ? <p className="members-onboarding-welcome__error">{error}</p> : null}

          {!loading ? (
            <>
              <p className="members-onboarding-welcome__eyebrow">{copy.titleTop}</p>
              <h1 className="members-onboarding-welcome__title">
                <span className="members-onboarding-welcome__title-stack">
                  <span className="members-onboarding-welcome__brand-a">{copy.titleBrandA}</span>
                  <span className="members-onboarding-welcome__brand-spacer"> </span>
                  <span className="members-onboarding-welcome__brand-b">{copy.titleBrandB}</span>
                </span>
              </h1>
              <p className="members-onboarding-welcome__tagline">{copy.subtitle}</p>
              <p className="members-onboarding-welcome__lead">{copy.description}</p>
              <button type="button" className="members-onboarding-welcome__cta" onClick={onStart}>
                <span className="members-onboarding-welcome__cta-label">Start onboarding</span>
                <span className="members-onboarding-welcome__cta-arrow" aria-hidden>
                  →
                </span>
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
